import { z } from "zod";

/**
 * The Statsig gate the whole suggestion surface sits behind.
 *
 * Lives here because both sides read it and this directory is symlinked into
 * `web/`, the same way `SIGNUP_STRAIGHT_IN_GATE` does. A string literal
 * copied into two packages is a gate that silently half-ships the day someone
 * fixes a typo in one of them.
 *
 * Checked in two places on purpose. The server refuses to sample, which is what
 * actually decides the feature is off; the client skips the request, which
 * keeps a gated-off page from spending one. With the gate closed the composer
 * falls through to the three chips it had before any of this — the off state is
 * the old behaviour, not a degraded new one.
 */
export const NEW_CHAT_TASK_SUGGESTIONS_GATE = "new_chat_task_suggestions";

/**
 * The Statsig gate that picks an account's suggestions for it instead of
 * drawing them at random: a scoring call against the person on every report of
 * what was shown, and a memory of the last 25 shown.
 *
 * Separate from `onboarding-v3` so the two ramp on their own. The onboarding
 * flow decides whether a new account lands on this screen; this decides only
 * how an open screen is filled. The server alone reads it. A client learns the
 * outcome from the response's `personalized` flag, and reports what it showed
 * on that signal rather than on a gate read of its own that could disagree
 * with the server's.
 */
export const CHAT_TASK_SUGGESTIONS_V2_GATE = "chat_task_suggestions_v2";

/**
 * The token a `shortPrompt` carries where a platform name belongs.
 *
 * Part of the wire contract, not an api-internal detail: the server sends
 * prompts containing it and the client is what substitutes. Defined here, in
 * the directory symlinked into `web/` and `native/`, because a copy on each
 * side is a constant that must not drift and has nothing enforcing that it
 * does not — change it to `{service}` in one package and the other silently
 * stops substituting and renders the token to the user.
 */
export const PLATFORM_SLOT = "{platform}";

/**
 * One suggestion card on the New Chat page: an id and its display text, and
 * nothing the server keeps for itself. Keeping a card to that leaves the server
 * free to decide what an id means, rather than freezing catalog content to
 * whatever frontend happens to be deployed.
 *
 * `requiredConnectors` is absent for that reason. It documents what a task
 * reaches, for whoever edits the catalog, not anything a card renders — a
 * client has no use for it, and shipping it would invite one.
 */
export const suggestedTaskResp = z.object({
  /**
   * Stable across catalog regenerations, so tap analytics accumulate against a
   * task rather than against whatever happened to occupy a slot that week.
   */
  id: z.string(),
  /** The card text, and the message text when the user taps it. */
  shortPrompt: z.string(),
  /**
   * A few platforms this task could run on, for the card to show as examples.
   *
   * Unlike the requirement fields, this one is here precisely because it is
   * display copy — rendered as written, so it needs no lookup table in `web/`
   * that could fall behind the catalog. Empty where okthx has only one provider
   * for the job, since there is no breadth to illustrate.
   */
  examplePlatforms: z.array(z.string()),
  /**
   * The glyph beside the prompt, chosen per task.
   *
   * This one travels, where the grid's Lucide icons could not: an emoji is a
   * string, so the argument for keeping icons client-side does not apply. It
   * rides along for the same reason `label` does on a category, which is that
   * adding a row must not need a frontend deploy to look right. Optional, so a
   * frontend that predates it keeps its own fallback.
   */
  emoji: z.string().optional(),
  /**
   * True on a task picked for this user rather than drawn at random. Picks come
   * first, and a client keeps them first.
   */
  picked: z.boolean().optional(),
});

/** A group of cards under one heading. */
export const suggestedTaskCategoryResp = z.object({
  id: z.string(),
  /**
   * Rendered as the visible heading, so it travels with the data rather than
   * living in a map in `web/`. A category added later must not need a frontend
   * deploy to show up.
   */
  label: z.string(),
  /** Shown when a task carries no emoji of its own. */
  emoji: z.string().optional(),
  tasks: z.array(suggestedTaskResp),
});

/**
 * `GET /api/a/suggested-tasks`.
 *
 * A fresh random sample per request — the server picks the categories and the
 * cards. Callers should hold the result still for the lifetime of the view
 * rather than refetching, or the grid reshuffles while the user is reaching
 * for a card.
 */
export const getSuggestedTasksResp = z.object({
  categories: z.array(suggestedTaskCategoryResp),
  /**
   * True when this account's suggestions are picked for it, so the client
   * reports what it showed. On the response rather than inferred from a
   * `picked` task: an account's first draw in the feature, or its first after
   * a stored set lapsed, is random, and that draw's report is what starts the
   * picking. A client that reported only picked draws would leave such an
   * account on random draws for good. Absent on a draw outside the feature.
   */
  personalized: z.boolean().optional(),
});

/**
 * The most tasks one `limit` request can ask for: the grid's own six rows of
 * three. The catalog runs to hundreds, and a client with a use for more than a
 * screenful of suggestions is a client paging through the catalog.
 */
export const MAX_SUGGESTED_TASKS_LIMIT = 18;

/**
 * Query for `GET /api/a/suggested-tasks`.
 *
 * `limit` asks for that many tasks, drawn from the whole catalog, for a surface
 * that shows a short list rather than the grid (native's new chat). They come
 * back grouped under their categories like the grid's, so there is one
 * response shape; a category holds however many of its tasks the draw landed
 * on. Without it the response is the grid.
 */
export const getSuggestedTasksQuery = z.object({
  limit: z.coerce.number().int().min(1).max(MAX_SUGGESTED_TASKS_LIMIT).optional(),
});

/**
 * `POST /api/a/suggested-tasks/shown`: the tasks a client just put on screen.
 *
 * In the gate that picks suggestions (`CHAT_TASK_SUGGESTIONS_V2_GATE`) the
 * server remembers them, so they are not suggested again soon, and picks the
 * next visit's set without them; outside it the report is a no-op. The GET that
 * served them only reads, so a cross-site request cannot change what a user is
 * shown.
 */
export const recordSuggestedTasksShownReq = z.object({
  taskIds: z.array(z.string().min(1)).min(1).max(MAX_SUGGESTED_TASKS_LIMIT),
});

export type SuggestedTaskResp = z.infer<typeof suggestedTaskResp>;
export type RecordSuggestedTasksShownReq = z.infer<typeof recordSuggestedTasksShownReq>;
export type SuggestedTaskCategoryResp = z.infer<typeof suggestedTaskCategoryResp>;
export type GetSuggestedTasksResp = z.infer<typeof getSuggestedTasksResp>;
export type GetSuggestedTasksQuery = z.infer<typeof getSuggestedTasksQuery>;
